import React, { useState, useRef, forwardRef, useImperativeHandle } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiBookmark, FiPlay } = FiIcons;

const VideoPlayer = forwardRef(({ url, onAddBookmark, bookmarks = [] }, ref) => {
  const [showBookmarkForm, setShowBookmarkForm] = useState(false);
  const [bookmarkNote, setBookmarkNote] = useState('');
  const iframeRef = useRef(null);
  const [currentTime, setCurrentTime] = useState(0);

  // Extract video ID and platform
  const getVideoInfo = (url) => {
    if (url.includes('youtube.com') || url.includes('youtu.be')) {
      const videoId = url.includes('youtu.be') 
        ? url.split('/').pop().split('?')[0]
        : url.split('v=')[1]?.split('&')[0];
      return { platform: 'youtube', id: videoId };
    }
    
    if (url.includes('vimeo.com')) {
      const videoId = url.split('/').pop().split('?')[0];
      return { platform: 'vimeo', id: videoId };
    }
    
    return { platform: 'other', id: null };
  };

  const videoInfo = getVideoInfo(url);

  // Expose methods to parent component
  useImperativeHandle(ref, () => ({
    seekTo: (time) => {
      // For YouTube and Vimeo, we'd need to use their respective APIs
      // This is a simplified implementation
      setCurrentTime(time);
      
      if (videoInfo.platform === 'youtube' && window.YT) {
        // YouTube API integration would go here
      } else if (videoInfo.platform === 'vimeo' && window.Vimeo) {
        // Vimeo API integration would go here
      }
    }
  }));

  const handleAddBookmark = () => {
    // In a real implementation, you'd get the current time from the video player
    // For now, we'll use a mock current time
    const timestamp = Math.floor(Math.random() * 600); // Mock timestamp
    onAddBookmark(timestamp, bookmarkNote);
    setBookmarkNote('');
    setShowBookmarkForm(false);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getEmbedUrl = (url) => {
    if (videoInfo.platform === 'youtube') {
      return `https://www.youtube.com/embed/${videoInfo.id}?enablejsapi=1&origin=${window.location.origin}`;
    } else if (videoInfo.platform === 'vimeo') {
      return `https://player.vimeo.com/video/${videoInfo.id}?api=1`;
    }
    return url;
  };

  return (
    <div className="space-y-4">
      {/* Video Container */}
      <div className="relative bg-black rounded-lg overflow-hidden">
        <div className="aspect-video">
          <iframe
            ref={iframeRef}
            src={getEmbedUrl(url)}
            title="Course Video"
            className="w-full h-full"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>

        {/* Video Controls Overlay */}
        <div className="absolute bottom-4 right-4">
          <motion.button
            onClick={() => setShowBookmarkForm(true)}
            className="bg-black bg-opacity-50 text-white p-2 rounded-full hover:bg-opacity-70 transition-colors"
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
          >
            <SafeIcon icon={FiBookmark} className="w-4 h-4" />
          </motion.button>
        </div>
      </div>

      {/* Bookmark Form */}
      {showBookmarkForm && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white border border-gray-200 rounded-lg p-4 shadow-lg"
        >
          <h4 className="font-medium text-gray-900 mb-3">Add Bookmark</h4>
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Note (optional)
              </label>
              <textarea
                value={bookmarkNote}
                onChange={(e) => setBookmarkNote(e.target.value)}
                placeholder="Add a note for this bookmark..."
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-nutrition-500 focus:border-nutrition-500"
                rows={3}
              />
            </div>
            <div className="flex justify-end space-x-2">
              <button
                onClick={() => setShowBookmarkForm(false)}
                className="px-3 py-2 text-sm text-gray-600 hover:text-gray-800"
              >
                Cancel
              </button>
              <button
                onClick={handleAddBookmark}
                className="px-4 py-2 bg-nutrition-600 text-white text-sm rounded-md hover:bg-nutrition-700 transition-colors"
              >
                Add Bookmark
              </button>
            </div>
          </div>
        </motion.div>
      )}

      {/* Bookmarks List */}
      {bookmarks.length > 0 && (
        <div className="bg-gray-50 rounded-lg p-4">
          <h4 className="font-medium text-gray-900 mb-3">Bookmarks</h4>
          <div className="space-y-2">
            {bookmarks.map((bookmark) => (
              <button
                key={bookmark.id}
                onClick={() => ref?.current?.seekTo(bookmark.timestamp)}
                className="w-full text-left p-2 bg-white rounded border border-gray-200 hover:border-nutrition-300 transition-colors"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2">
                      <SafeIcon icon={FiPlay} className="w-3 h-3 text-nutrition-600" />
                      <span className="text-sm font-medium text-nutrition-600">
                        {formatTime(bookmark.timestamp)}
                      </span>
                    </div>
                    {bookmark.note && (
                      <p className="text-sm text-gray-600 mt-1">{bookmark.note}</p>
                    )}
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
});

VideoPlayer.displayName = 'VideoPlayer';

export default VideoPlayer;